import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "next-themes";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CircleUserRound, LogOut, Settings as SettingsIcon, RotateCw, Menu, ChevronDown, Sun, Moon } from "lucide-react";
import { useAuth } from "@/state/AuthProvider";
import { useAppState } from "@/state/AppStateProvider";
import { toast } from "sonner";

function crumbsFromPath(pathname) {
  const map = {
    "/dashboard": ["Dashboard"],
    "/create": ["Create"],
    "/projects": ["Projects"],
    "/inbox": ["Content Inbox"],
    "/calendar": ["Calendar"],
    "/library": ["Library"],
    "/skills": ["Skills"],
    "/integrations": ["Integrations"],
    "/settings": ["Settings"],
    "/manual-post": ["Create", "Manual Post"],
    "/blog": ["Workspace", "Blog Studio"],
    "/repurpose": ["Create", "Repurpose"],
  };
  if (map[pathname]) return map[pathname];
  if (pathname.startsWith("/runs")) return ["Create", "Agent Run"];
  if (pathname.startsWith("/projects/")) return ["Projects", "Detail"];
  if (pathname.startsWith("/library/")) return ["Library", "Asset"];
  return ["Workspace"];
}

function ThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const isDark = resolvedTheme === "dark";
  return (
    <button
      type="button"
      data-testid="topbar-theme-toggle"
      onClick={() => setTheme(isDark ? "light" : "dark")}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      title={isDark ? "Light mode" : "Dark mode"}
      className="flex h-9 w-9 items-center justify-center rounded-full border text-[color:var(--chrome-text-muted)] transition-colors hover:bg-[color:var(--chrome-hover)] hover:text-[color:var(--chrome-text)]"
      style={{ borderColor: "var(--chrome-border)" }}
    >
      {/* Render icon only after mount to avoid a theme-mismatch flash. */}
      {mounted ? (isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />) : <Sun className="h-4 w-4 opacity-0" />}
    </button>
  );
}

export function TopBar({ onOpenMobileSidebar }) {
  const { user, logout } = useAuth();
  const { resetMockData } = useAppState();
  const location = useLocation();
  const crumbs = crumbsFromPath(location.pathname);

  return (
    <header
      data-testid="topbar"
      className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: "var(--chrome)", borderColor: "var(--chrome-border)", boxShadow: "0 1px 0 var(--chrome-border)" }}
    >
      <button
        type="button"
        data-testid="topbar-mobile-menu"
        onClick={onOpenMobileSidebar}
        aria-label="Open navigation"
        className="flex h-9 w-9 items-center justify-center rounded-lg border text-[color:var(--chrome-text-muted)] transition-colors hover:text-[color:var(--chrome-text)] md:hidden"
        style={{ backgroundColor: "var(--chrome-hover)", borderColor: "var(--chrome-border)" }}
      >
        <Menu className="h-4 w-4" />
      </button>

      <div className="flex min-w-0 items-center gap-2 text-sm">
        {crumbs.map((crumb, idx) => (
          <React.Fragment key={idx}>
            <span className={cn("truncate", idx === crumbs.length - 1 ? "text-[color:var(--chrome-text)] font-medium" : "text-[color:var(--chrome-text-muted)]")}>{crumb}</span>
            {idx < crumbs.length - 1 && <span className="text-[color:var(--chrome-text-muted)]">/</span>}
          </React.Fragment>
        ))}
      </div>

      <div className="ml-auto flex items-center gap-2">
        <ThemeToggle />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="topbar-user-menu"
              className="flex h-9 items-center gap-2 rounded-full border pl-1 pr-3 text-sm font-medium text-[color:var(--chrome-text)] transition-colors hover:bg-[color:var(--chrome-hover)]"
              style={{ borderColor: "var(--chrome-border)" }}
              aria-label="User menu"
            >
              <Avatar className="h-7 w-7">
                <AvatarFallback className="bg-[color:var(--accent-blue)] text-xs font-semibold text-[color:var(--accent-blue-ink)]">
                  {user?.initials || "AA"}
                </AvatarFallback>
              </Avatar>
              <span className="hidden sm:inline">{user?.name || "Account"}</span>
              <ChevronDown className="hidden h-3.5 w-3.5 text-[color:var(--chrome-text-muted)] sm:block" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-1">{user?.name || "Account"}</span>
                <span className="text-xs text-muted-foreground">{user?.email || "Signed in"}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/settings" className="flex items-center gap-2">
                <SettingsIcon className="h-3.5 w-3.5" /> Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/brands" className="flex items-center gap-2">
                <CircleUserRound className="h-3.5 w-3.5" /> Brands
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                resetMockData();
                toast.success("Local workspace reset to defaults");
              }}
              data-testid="topbar-reset-mock-data"
            >
              <RotateCw className="mr-2 h-3.5 w-3.5" /> Reset local data
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => {
                logout();
                toast.message("Signed out");
              }}
              data-testid="topbar-logout"
              className="text-[color:var(--accent-red)] focus:text-[color:var(--accent-red)]"
            >
              <LogOut className="mr-2 h-3.5 w-3.5" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
